// src/components/Events/EventLocationLink.js
import React, { memo, useState } from 'react';
import styled from 'styled-components';
import { MapPin, LocationEdit } from 'lucide-react';
import { withRole } from '../Common/withRole';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { EditIconButton, PrimaryButton, DefaultButton } from '../Common/BaseButton';
import { updateEventLocation, makeGoogleApiRequest } from '../../services/supabase';

const EventLocation = styled.span`
  color: #666;
  margin-bottom: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const MapLink = styled.a`
  color: var(--primary-green);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  font-size: 1.1rem;
  gap: 0.3rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;


const EditingLocationContainer = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  max-width: 600px;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0.5rem;
    flex-wrap: wrap;  
  }
`;

const SelectContainer = styled.div`
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SaveCancelButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: 0.5rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0.5rem;
  }
`;

const ReadOnlyLocation = memo(({ event }) => {
  return (
    <EventLocation>
      {event.location_lat && event.location_lng && (
        <MapLink 
          href={`https://www.google.com/maps/search/?api=1&query=${event.location_lat},${event.location_lng}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in Google Maps"
        >
          <MapPin size={16} style={{color: 'var(--primary-green)'}} /> {event.location}
        </MapLink>
      )}
    </EventLocation>
  );
});

const EditableLocation = memo(({ 
  event, 
  isEditingLocation, 
  editedLocationValue, 
  handleLocationSelect, 
  handleCancelLocationEdit, 
  savingLocation, 
  editedLocation,
  handleEditLocation,
  onSuccess
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [editedLocationCoords, setEditedLocationCoords] = useState({
    lat: null,
    lng: null,
    placeId: null
  });

  const updateGoogleEventLocation = async (googleEventId, newLocation, startTime, endTime, timezone) => {
    const response = await makeGoogleApiRequest(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${googleEventId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          location: newLocation,
          start: { dateTime: startTime, timeZone: timezone },
          end: { dateTime: endTime, timeZone: timezone },
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update Google Calendar event location');
    }
  };

  const handleSaveLocation = async () => {
    try {
      setIsSaving(true);
      // --- UPDATE GOOGLE CALENDAR LOCATION FIRST ---
      if (event.google_event_id) {
        try {
          await updateGoogleEventLocation(
            event.google_event_id,
            editedLocation,
            event.start_time,
            event.end_time,
            editedLocationCoords.timezone
          );
        } catch (err) {
          alert('Failed to update Google Calendar event location.');
          console.error(err);
          return;
        }
      }

      // If Google Calendar update succeeded (or there is no Google event), update DB
      const locationData = {
        address: editedLocation,
        lat: editedLocationCoords.lat,
        lng: editedLocationCoords.lng,
        placeId: editedLocationCoords.placeId,
        timezone: editedLocationCoords.timezone
      };

      const { data, error } = await updateEventLocation(event.id, locationData);

      if (error) throw error;

      // Call onSuccess with updated event data
      onSuccess({
        ...event,
        location: editedLocation,
        location_lat: editedLocationCoords.lat,
        location_lng: editedLocationCoords.lng,
        location_place_id: editedLocationCoords.placeId,
        timezone: editedLocationCoords.timezone
      });

      handleCancelLocationEdit();
    } catch (err) {
      console.error('Failed to update location:', err);
      alert('Failed to update location. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditingLocation) {
    return (
      <EditingLocationContainer>
        <SelectContainer>
          <GooglePlacesAutocomplete
            style={{maxWidth:'400px'}}
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            selectProps={{
              value: editedLocationValue,
              onChange: (place) => {
                handleLocationSelect(place);
                if (place && place.value) {
                  const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
                  placesService.getDetails(
                    {
                      placeId: place.value.place_id,
                      fields: ['geometry', 'formatted_address', 'utc_offset_minutes']
                    },
                    (placeResult, status) => {
                      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                        // Get timezone from the location
                        const timezone = getTimezoneFromOffset(placeResult.utc_offset_minutes);
                        
                        setEditedLocationCoords({
                          lat: placeResult.geometry.location.lat(),
                          lng: placeResult.geometry.location.lng(),
                          placeId: place.value.place_id,
                          timezone: timezone
                        });
                      }
                    }
                  );
                }
              },
              placeholder: 'Enter event location',
              isClearable: true,
              styles: {
                control: (provided) => ({
                  ...provided,
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  minWidth: '350px',
                  '&:hover': {
                    borderColor: '#2D882D'
                  }
                }),
                menu: (provided) => ({
                  ...provided,
                  borderRadius: '4px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 9999
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#2D882D' 
                    : state.isFocused 
                      ? 'rgba(45, 136, 45, 0.1)' 
                      : 'white',
                  color: state.isSelected ? 'white' : '#333',
                  cursor: 'pointer'
                })
              }
            }}
          />
        </SelectContainer>
        <SelectContainer>
          <SaveCancelButtons>
            <DefaultButton onClick={handleCancelLocationEdit}>
              Cancel
            </DefaultButton>
            <PrimaryButton 
              onClick={handleSaveLocation}
              disabled={isSaving || !editedLocation}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </PrimaryButton>
          </SaveCancelButtons>
        </SelectContainer>
      </EditingLocationContainer>
    );
  }

  return (
    <EventLocation>
      {event.location_lat && event.location_lng && (
        <>
          <MapLink 
            href={`https://www.google.com/maps/search/?api=1&query=${event.location_lat},${event.location_lng}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in Google Maps"
          >
            <MapPin size={16} style={{color: 'var(--primary-green)'}} /> {event.location}
          </MapLink>
          <EditIconButton onClick={handleEditLocation} title="Edit location">
            <LocationEdit size={18} />
          </EditIconButton>
        </>
      )}
    </EventLocation>
  );
});

// Helper function to convert UTC offset to timezone string
const getTimezoneFromOffset = (offsetMinutes) => {
  const hours = Math.abs(Math.floor(offsetMinutes / 60));
  const minutes = Math.abs(offsetMinutes % 60);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  
  // Map to common timezone strings
  const timezoneMap = {
    '-5:00': 'America/New_York',
    '-6:00': 'America/Chicago',
    '-7:00': 'America/Denver',
    '-8:00': 'America/Los_Angeles',
    // Add more mappings as needed
  };

  const offsetString = `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  return timezoneMap[offsetString] || 'America/New_York'; // Default to EST if not found
};

const WithRoleLocation = withRole(EditableLocation, {
  managerOnly: true,
  fallback: ReadOnlyLocation
});

const EventLocationLink = memo(({ 
  event, 
  isEditingLocation, 
  editedLocationValue, 
  handleLocationSelect, 
  handleCancelLocationEdit, 
  savingLocation, 
  editedLocation,
  handleEditLocation,
  isManager,
  isAuthenticated,
  onSuccess
}) => {
  return (
    <WithRoleLocation
      isManager={isManager}
      isAuthenticated={isAuthenticated}
      event={event}
      isEditingLocation={isEditingLocation}
      editedLocationValue={editedLocationValue}
      handleLocationSelect={handleLocationSelect}
      handleCancelLocationEdit={handleCancelLocationEdit}
      savingLocation={savingLocation}
      editedLocation={editedLocation}
      handleEditLocation={handleEditLocation}
      onSuccess={onSuccess}
    />
  );
});

export default EventLocationLink;